import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2, Sparkles, Pencil, Check, X, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    course_id: "", week_number: 1, type: "mcq", difficulty: "easy", question: "", options: ["", "", "", ""], correct_answer: "", audio_text: ""
  });

  // AI Generation state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCourse, setAiCourse] = useState("");
  const [aiWeek, setAiWeek] = useState("1");
  const [aiCount, setAiCount] = useState("5");
  const [generating, setGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [coursesRes] = await Promise.all([axios.get(`${API}/courses`)]);
      setCourses(coursesRes.data);
      if (coursesRes.data.length > 0) {
        setSelectedCourse(coursesRes.data[0].id);
        loadAssignments(coursesRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async (courseId) => {
    try {
      const response = await axios.get(`${API}/assignments/course/${courseId}`);
      setAssignments(response.data);
    } catch (error) {
      setAssignments([]);
    }
  };

  const createAssignment = async (e) => {
    e.preventDefault();
    if (!formData.course_id || !formData.question || !formData.correct_answer) return toast.error("Fill required fields");
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        ...formData,
        week_number: parseInt(formData.week_number),
        options: formData.type === "mcq" ? formData.options.filter(o => o.trim()) : null,
        audio_text: formData.type === "listening" ? formData.audio_text : null
      };
      await axios.post(`${API}/assignments`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Assignment created!");
      setDialogOpen(false);
      loadAssignments(selectedCourse);
    } catch (error) {
      toast.error("Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const deleteAssignment = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/assignments/${deleteTarget}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      setDeleteTarget(null);
      loadAssignments(selectedCourse);
    } catch (error) {
      toast.error("Failed");
    }
  };

  const updateOption = (i, v) => {
    const opts = [...formData.options];
    opts[i] = v;
    setFormData({...formData, options: opts});
  };

  // AI Generation functions
  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error("Enter a prompt");
    if (!aiCourse) return toast.error("Select a course");
    setGenerating(true);
    setGeneratedQuestions([]);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/admin/generate-questions`, {
        prompt: aiPrompt,
        course_id: aiCourse,
        week_number: parseInt(aiWeek),
        count: parseInt(aiCount),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGeneratedQuestions(res.data.questions);
      toast.success(`Generated ${res.data.questions.length} questions!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const updateGenerated = (idx, field, value) => {
    setGeneratedQuestions(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const updateGeneratedOption = (qIdx, optIdx, value) => {
    setGeneratedQuestions(prev => {
      const updated = [...prev];
      const opts = [...(updated[qIdx].options || [])];
      opts[optIdx] = value;
      updated[qIdx] = { ...updated[qIdx], options: opts };
      return updated;
    });
  };

  const removeGenerated = (idx) => {
    setGeneratedQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const saveAllGenerated = async () => {
    if (generatedQuestions.length === 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/admin/save-generated-questions`, {
        questions: generatedQuestions,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved ${res.data.saved} questions!`);
      setGeneratedQuestions([]);
      setAiOpen(false);
      setAiPrompt("");
      loadAssignments(selectedCourse);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const typeBadge = (t) => {
    const colors = { mcq: "bg-[#B4F8C8]", fill_blank: "bg-[#FBE7C6]", translation: "bg-[#D6E6FA]", listening: "bg-[#FFAEBC]" };
    return colors[t] || "bg-[#F0EFEB]";
  };

  const diffBadge = (d) => {
    const colors = { easy: "text-green-700 bg-green-100", medium: "text-amber-700 bg-amber-100", hard: "text-red-700 bg-red-100" };
    return colors[d] || "bg-[#F0EFEB]";
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Assignments</h1>
          <p className="text-[#4A4A4A]">Weekly assignments for courses</p>
        </div>
        <div className="flex gap-3">
          {/* AI Generate Button */}
          <button
            onClick={() => { setAiOpen(true); setAiCourse(selectedCourse); }}
            className="brutalist-btn flex items-center gap-2 bg-gradient-to-r from-[#3C8DE4] to-[#6B5CE7] text-white border-2 border-[#0A0A0A] px-4 py-2 rounded-lg font-bold shadow-[3px_3px_0_#0A0A0A] hover:shadow-[1px_1px_0_#0A0A0A] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            data-testid="ai-generate-btn"
          >
            <Sparkles className="w-5 h-5" /> AI Generate
          </button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button className="brutalist-btn-primary flex items-center gap-2"><Plus className="w-5 h-5" />Add Assignment</button>
            </DialogTrigger>
            <DialogContent className="brutalist-card p-0 border-2 border-[#0A0A0A] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader className="p-6 border-b-2 border-[#0A0A0A]">
                <DialogTitle>Add Assignment</DialogTitle>
              </DialogHeader>
              <form onSubmit={createAssignment} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Course *</Label>
                    <Select value={formData.course_id} onValueChange={v => setFormData({...formData, course_id: v})}>
                      <SelectTrigger className="brutalist-input mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Week *</Label>
                    <Select value={String(formData.week_number)} onValueChange={v => setFormData({...formData, week_number: v})}>
                      <SelectTrigger className="brutalist-input mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>{[1,2,3,4,5,6,7,8].map(w => <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type *</Label>
                    <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                      <SelectTrigger className="brutalist-input mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcq">Multiple Choice</SelectItem>
                        <SelectItem value="fill_blank">Fill in Blank</SelectItem>
                        <SelectItem value="translation">Translation</SelectItem>
                        <SelectItem value="listening">Listening</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Difficulty *</Label>
                    <Select value={formData.difficulty} onValueChange={v => setFormData({...formData, difficulty: v})}>
                      <SelectTrigger className="brutalist-input mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Question *</Label>
                  <Textarea value={formData.question} onChange={e => setFormData({...formData, question: e.target.value})} className="brutalist-input mt-2" rows={2} />
                </div>
                {formData.type === "mcq" && (
                  <div>
                    <Label>Options</Label>
                    <div className="space-y-2 mt-2">{formData.options.map((o,i) => <Input key={i} value={o} onChange={e => updateOption(i, e.target.value)} placeholder={`Option ${i+1}`} className="brutalist-input" />)}</div>
                  </div>
                )}
                {formData.type === "listening" && (
                  <div>
                    <Label>Audio Text</Label>
                    <Textarea value={formData.audio_text} onChange={e => setFormData({...formData, audio_text: e.target.value})} className="brutalist-input mt-2" rows={2} />
                  </div>
                )}
                <div>
                  <Label>Correct Answer *</Label>
                  <Input value={formData.correct_answer} onChange={e => setFormData({...formData, correct_answer: e.target.value})} className="brutalist-input mt-2" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <button type="submit" disabled={creating} className="brutalist-btn-primary">{creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create"}</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Course Filter */}
      <div className="mb-6">
        <Select value={selectedCourse} onValueChange={v => { setSelectedCourse(v); loadAssignments(v); }}>
          <SelectTrigger className="brutalist-input w-64"><SelectValue placeholder="Select course" /></SelectTrigger>
          <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {assignments.length === 0 ? (
        <div className="brutalist-card p-12 text-center">
          <p className="text-[#4A4A4A]">No assignments for this course</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[1,2,3,4,5,6,7,8].map(week => {
            const weekAssignments = assignments.filter(a => a.week_number === week);
            if (weekAssignments.length === 0) return null;
            return (
              <div key={week} className="brutalist-card overflow-hidden">
                <div className="bg-[#F0EFEB] p-3 border-b-2 border-[#0A0A0A] font-bold">Week {week}</div>
                <div className="p-4 space-y-2">
                  {weekAssignments.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-white border border-[#0A0A0A] rounded">
                      <div>
                        <span className="font-medium">{a.question}</span>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-[#F0EFEB] rounded">{a.type}</span>
                          <span className="text-xs px-2 py-0.5 bg-[#F0EFEB] rounded">{a.difficulty}</span>
                        </div>
                      </div>
                      <button onClick={() => setDeleteTarget(a.id)} className="p-2 text-[#FF6B6B]" data-testid={`delete-assignment-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Generation Dialog */}
      <Dialog open={aiOpen} onOpenChange={(o) => { setAiOpen(o); if (!o) { setGeneratedQuestions([]); setEditingIdx(null); } }}>
        <DialogContent className="brutalist-card p-0 border-2 border-[#0A0A0A] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="p-6 border-b-2 border-[#0A0A0A] bg-gradient-to-r from-[#3C8DE4] to-[#6B5CE7]">
            <DialogTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI Question Generator</DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-5">
            {/* Config Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Course</Label>
                <Select value={aiCourse} onValueChange={setAiCourse}>
                  <SelectTrigger className="brutalist-input mt-1" data-testid="ai-course-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Week</Label>
                <Select value={aiWeek} onValueChange={setAiWeek}>
                  <SelectTrigger className="brutalist-input mt-1" data-testid="ai-week-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4,5,6,7,8].map(w => <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Questions</Label>
                <Select value={aiCount} onValueChange={setAiCount}>
                  <SelectTrigger className="brutalist-input mt-1" data-testid="ai-count-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[3,5,8,10,15,20].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Prompt */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Prompt</Label>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. Generate 5 beginner French vocabulary questions about greetings. Mix MCQ and fill-in-the-blank. Include easy and medium difficulty."
                className="brutalist-input mt-1"
                rows={3}
                data-testid="ai-prompt-input"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="brutalist-btn-primary flex items-center gap-2 w-full justify-center"
              data-testid="ai-generate-submit-btn"
            >
              {generating ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Generating with Claude...</>
              ) : (
                <><Sparkles className="w-5 h-5" /> Generate Questions</>
              )}
            </button>

            {/* Generated Questions Review */}
            {generatedQuestions.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">Review & Edit ({generatedQuestions.length} questions)</h3>
                  <button
                    onClick={saveAllGenerated}
                    disabled={saving}
                    className="brutalist-btn-primary flex items-center gap-2"
                    data-testid="ai-save-all-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save All to Course
                  </button>
                </div>

                {generatedQuestions.map((q, idx) => (
                  <div key={idx} className="brutalist-card p-4 border-2 border-[#0A0A0A]" data-testid={`generated-question-${idx}`}>
                    {editingIdx === idx ? (
                      /* Editing Mode */
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <Label className="text-xs">Type</Label>
                            <Select value={q.type} onValueChange={v => updateGenerated(idx, "type", v)}>
                              <SelectTrigger className="brutalist-input mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mcq">MCQ</SelectItem>
                                <SelectItem value="fill_blank">Fill Blank</SelectItem>
                                <SelectItem value="translation">Translation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs">Difficulty</Label>
                            <Select value={q.difficulty} onValueChange={v => updateGenerated(idx, "difficulty", v)}>
                              <SelectTrigger className="brutalist-input mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="easy">Easy</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="hard">Hard</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Question</Label>
                          <Textarea value={q.question} onChange={e => updateGenerated(idx, "question", e.target.value)} className="brutalist-input mt-1 text-sm" rows={2} />
                        </div>
                        {q.type === "mcq" && q.options && (
                          <div>
                            <Label className="text-xs">Options</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              {q.options.map((opt, oi) => (
                                <Input key={oi} value={opt} onChange={e => updateGeneratedOption(idx, oi, e.target.value)} className="brutalist-input text-sm h-8" placeholder={`Option ${oi+1}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Correct Answer</Label>
                          <Input value={q.correct_answer} onChange={e => updateGenerated(idx, "correct_answer", e.target.value)} className="brutalist-input mt-1 text-sm h-8" />
                        </div>
                        <button onClick={() => setEditingIdx(null)} className="text-sm text-[#3C8DE4] font-bold flex items-center gap-1">
                          <Check className="w-4 h-4" /> Done Editing
                        </button>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-[#888]">Q{idx + 1}</span>
                            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${typeBadge(q.type)}`}>{q.type}</span>
                            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${diffBadge(q.difficulty)}`}>{q.difficulty}</span>
                          </div>
                          <p className="font-medium text-sm">{q.question}</p>
                          {q.type === "mcq" && q.options && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {q.options.map((opt, oi) => (
                                <span key={oi} className={`text-xs px-2 py-1 rounded border ${opt === q.correct_answer ? "border-green-500 bg-green-50 font-bold" : "border-[#ccc]"}`}>
                                  {opt}
                                </span>
                              ))}
                            </div>
                          )}
                          {q.type !== "mcq" && (
                            <p className="text-xs mt-1 text-green-700 font-semibold">Answer: {q.correct_answer}</p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setEditingIdx(idx)} className="p-1.5 hover:bg-[#F0EFEB] rounded" data-testid={`edit-generated-${idx}`}>
                            <Pencil className="w-4 h-4 text-[#3C8DE4]" />
                          </button>
                          <button onClick={() => removeGenerated(idx)} className="p-1.5 hover:bg-red-50 rounded" data-testid={`remove-generated-${idx}`}>
                            <X className="w-4 h-4 text-[#FF6B6B]" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Assignment?"
        description="This will permanently remove this assignment. This action cannot be undone."
        onConfirm={deleteAssignment}
        destructive
      />
    </div>
  );
};

export default AdminAssignments;
